import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const LABELS = ["A", "B", "C", "D"];

export const CurioQuiz = ({ slug }) => {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [res, setRes] = useState({});
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    setD(null); setRes({}); setFailed(false);
    api.get(`/facts/${slug}/quiz`).then((r) => setD(r.data)).catch(() => setFailed(true));
  }, [slug]);
  if (failed) return null;
  const answer = async (q, o) => {
    if (res[q.id] || busy) return;
    setBusy(q.id);
    try {
      const { data } = await api.post(`/facts/${slug}/quiz/check`, { id: q.id, answer: o });
      setRes((r) => ({ ...r, [q.id]: { ...data, pick: o } }));
    } catch {
      toast.error("Couldn't check that answer. Please try again");
    } finally { setBusy(null); }
  };
  const done = d && Object.keys(res).length === d.questions.length;
  const score = Object.values(res).filter((r) => r.correct).length;
  const cls = (q, o) => {
    const r = res[q.id];
    if (!r) return "bg-[#FAF7F0] border-[#D8CEB9] hover:border-[#9E4535] text-[#1A1615]";
    if (o === r.correct_answer) return "bg-[#9E4535] border-[#9E4535] text-[#FAF7F0]";
    if (o === r.pick) return "bg-[#F5E8EA] border-[#E4C9CD] text-[#7E3628] line-through";
    return "bg-[#FAF7F0] border-[#E7E0D2] text-[#A99F92]";
  };
  return (
    <section className="mt-12 bg-[#F3EDE0] border border-[#D8CEB9] border-t-4 border-t-[#9E4535] rounded-sm p-5 sm:p-7" data-testid="curio-quiz">
      <p className="caption-label !text-[#9E4535] flex items-center gap-2"><HelpCircle className="w-3.5 h-3.5" /> Test yourself · 3 questions</p>
      <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[#1A1615] mt-2">Were you paying attention?</h2>
      {!d ? (
        <div className="mt-5 space-y-3" data-testid="curio-quiz-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-sm bg-[#EAE2D1] animate-pulse" />)}
        </div>
      ) : (
        <ol className="mt-5 space-y-6">
          {d.questions.map((q, qi) => (
            <li key={q.id} data-testid={`curio-quiz-q-${qi}`}>
              <p className="font-ui font-semibold text-[15px] sm:text-base text-[#1A1615]">{qi + 1}. {q.question}</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {q.options.map((o, oi) => (
                  <button key={o} data-testid={`curio-quiz-q-${qi}-opt-${oi}`} disabled={!!res[q.id]} onClick={() => answer(q, o)}
                    className={`flex items-center gap-3 text-left rounded-sm border px-3 py-2.5 font-ui text-sm transition-[background-color,border-color,color] ${cls(q, o)}`}>
                    <span className="w-6 h-6 shrink-0 rounded-full border border-current/30 flex items-center justify-center text-[11px] font-bold opacity-80">{LABELS[oi]}</span>
                    <span>{o}</span>
                  </button>
                ))}
              </div>
              {res[q.id] && (
                <p data-testid={`curio-quiz-q-${qi}-result`} className="mt-2 font-ui text-[13px] text-[#4A4036] flex gap-1.5">
                  {res[q.id].correct ? <Check className="w-4 h-4 text-[#2D4A3E] shrink-0" /> : <X className="w-4 h-4 text-[#9E4535] shrink-0" />}
                  <span><b>{res[q.id].correct ? "Correct." : `It's ${res[q.id].correct_answer}.`}</b> {res[q.id].explanation}</span>
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
      {done && (
        <div className="mt-7 bg-[#1A1615] text-[#FAF7F0] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="curio-quiz-done">
          <div>
            <p className="font-serif-display text-2xl font-bold" data-testid="curio-quiz-score">{score}/3{score === 3 ? ", a perfect score" : ""}.</p>
            <p className="font-ui text-sm text-[#D8CEB9] mt-1">Fancy more? The Trivia Vault has thousands of {d.category.toLowerCase()} questions and a free Daily Three.</p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Link to="/trivia/play?source=daily&mode=mc&group=0" data-testid="curio-quiz-daily" className="inline-flex items-center gap-1.5 bg-[#FAF7F0] text-[#1A1615] hover:bg-white rounded-full px-4 py-2 font-ui text-sm font-semibold transition-colors">Daily Three</Link>
            <Link to={`/trivia/play?source=category&category=${encodeURIComponent(d.category)}&mode=mc&count=10`} data-testid="curio-quiz-category"
              className="inline-flex items-center gap-1.5 bg-[#9E4535] hover:bg-[#86392B] text-[#FAF7F0] rounded-full px-4 py-2 font-ui text-sm font-semibold transition-colors">
              {d.category} round <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </section>
  );
};
